import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaMapMarkerAlt } from "react-icons/fa";
import StripePayment from "./Stripe/StripePayment";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

// How long a purchase request is allowed to sit idle before we tell the user
// it looks slow, and the hard cutoff before we give up waiting entirely.
const TXN_SLOW_THRESHOLD_MS = 6000;
const TXN_TIMEOUT_MS = 20000;

const API_BASE = "https://xianhefairy.com/LotusFairy_API/api";

// Exchange rate: 1$ equals 10 Gems
const EXCHANGE_RATE = 10;

const DELIVERY_FEE_USD = 25;
const DELIVERY_FEE_GEMS = 250;
const FREE_DELIVERY_THRESHOLD_USD = 500;

// The purchase API answers HTTP 200 even when it rejects the payload: model
// binding failures come back as the literal string
// "System.Web.Http.ModelBinding.ModelStateDictionary" with a 200 status. Trusting
// res.ok alone reports a successful purchase and clears the cart for an order
// that was never recorded, so the body has to be checked too.
const assertApiSuccess = (label, res, text, { requireSuccessWord = false } = {}) => {
  if (!res.ok) throw new Error(`${label} failed (${res.status})`);

  if (text.includes("ModelStateDictionary")) {
    throw new Error(
      `${label} rejected the payload — server-side model binding failed`,
    );
  }

  let parsed = text;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Plain-text body; keep the raw string.
  }

  // A string body is this API's way of reporting an outcome in words, so it has
  // to actually say the purchase went through.
  if (
    requireSuccessWord &&
    typeof parsed === "string" &&
    !/success|completed/i.test(parsed)
  ) {
    throw new Error(`${label} did not confirm success: ${parsed}`);
  }

  return parsed;
};

const GOLD = "#DFBD69";
const GOLD_DIM = "rgba(223,189,105,0.7)";
const CARD_BG = "#0A031E";
const CARD_BORDER = "1px solid rgba(223,189,105,0.2)";

const styles = {
  page: {
    minHeight: "100vh",
    padding: "16px",
    fontFamily: "'DMSans', Arial, sans-serif",
    color: "#fff2d2",
  },
  wrapper: {
    maxWidth: "760px",
    margin: "0 auto",
  },
  // Desktop only: wider shell so a two-column layout has room, similar to
  // Lazada's checkout (item/delivery details on the left, a sticky payment +
  // summary sidebar on the right).
  desktopWrapper: {
    maxWidth: "1100px",
    margin: "0 auto",
  },
  desktopGrid: {
    display: "flex",
    alignItems: "flex-start",
    gap: "20px",
  },
  leftColumn: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  // Sticks to the top of the viewport while scrolling past the left column,
  // then scrolls away with the rest of the page once its content ends — so,
  // unlike the mobile bar, it never covers the site footer. Nothing else is
  // pinned above the scroll area here, so this only needs a small breathing
  // gap — anything taller than the page header's own height would shove the
  // sidebar down past where the left column starts.
  rightColumn: {
    flex: "0 0 360px",
    position: "sticky",
    top: "16px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
  },
  backBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: GOLD,
    fontSize: "20px",
    padding: "0 8px 0 0",
    lineHeight: 1,
  },
  heading: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 800,
    color: GOLD,
    fontFamily: "DMSans",
  },
  card: {
    backgroundColor: CARD_BG,
    border: CARD_BORDER,
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "12px",
  },
  sectionTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  sectionTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: 700,
    color: GOLD,
  },
  linkBtn: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: GOLD_DIM,
    fontSize: "13px",
    textDecoration: "underline",
    whiteSpace: "nowrap",
  },
  radio: {
    accentColor: GOLD,
    width: 16,
    height: 16,
    marginTop: 2,
    flexShrink: 0,
  },
  optionName: {
    color: GOLD,
    fontWeight: 600,
    fontSize: "13px",
  },
  optionMeta: {
    color: GOLD_DIM,
    fontSize: "12px",
    marginTop: 2,
    lineHeight: 1.5,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "12px",
    color: GOLD_DIM,
    fontSize: "13px",
    marginBottom: "8px",
  },
  // Only the label is allowed to wrap; the amount stays on a single line so it
  // never breaks between the number and its "Gems" unit.
  summaryLabel: {
    minWidth: 0,
  },
  summaryValue: {
    whiteSpace: "nowrap",
    flexShrink: 0,
    textAlign: "right",
  },
  // Sticks to the bottom of the viewport while the order is being reviewed, and
  // scrolls away with the page instead of covering the site footer.
  footerBar: {
    position: "sticky",
    bottom: 0,
    background: "#120A26",
    borderTop: "1px solid rgba(223,189,105,0.25)",
    padding: "12px 16px",
    margin: "0 -16px -16px",
    zIndex: 50,
  },
  footerInner: {
    maxWidth: "760px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid rgba(223,189,105,0.3)",
    background: "rgba(255,255,255,0.06)",
    color: GOLD,
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  },
  inputLabel: {
    color: GOLD_DIM,
    fontSize: "12px",
    display: "block",
    marginBottom: 4,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10001,
    padding: 16,
  },
  modal: {
    backgroundColor: "#1a1a1a",
    border: "1px solid rgba(223,189,105,0.25)",
    borderRadius: 12,
    padding: "28px 24px",
    width: "min(500px, 92vw)",
    textAlign: "center",
    boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
  },
  modalBtn: {
    background: GOLD,
    color: "#151517",
    border: "none",
    borderRadius: 8,
    padding: "10px 22px",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "0.95rem",
  },
};

// Selectable card used by the delivery options and the payment methods
const selectableRow = (selected) => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  border: selected ? `1px solid ${GOLD}` : "1px solid rgba(223,189,105,0.25)",
  background: selected ? "rgba(223,189,105,0.1)" : "transparent",
  borderRadius: "8px",
  padding: "12px",
});

// One-line rendering of a saved delivery address
const formatAddress = (address) =>
  [
    [address.Block, address.Street].filter(Boolean).join(" "),
    address.UnitNo,
    address.Postal,
    address.Country || "Singapore",
  ]
    .filter(Boolean)
    .join(", ");

// One-line rendering of the delivery address currently held in the form
const formatDeliveryAddress = (address) =>
  [
    [address.block, address.street].filter(Boolean).join(" "),
    address.unit,
    address.postal,
    "Singapore",
  ]
    .filter(Boolean)
    .join(", ");

// One-line rendering of a pickup store
const formatStore = (store) =>
  [
    [store.Block, store.Street].filter(Boolean).join(", "),
    store.UnitNo,
    store.Province,
    store.Postal,
    store.Country,
  ]
    .filter(Boolean)
    .join(", ");

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userID = localStorage.getItem("userID");

  // The cart page carries the applied voucher over. Like on the cart page it is
  // display-only — the amounts posted to the purchase API are unchanged.
  const discountPercent = Number(location.state?.discountPercent) || 0;

  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCoins, setCurrentCoins] = useState(0);

  // Same breakpoint Tryoutpage uses for its mobile/desktop split, so the two
  // layouts switch at the same width as the rest of the site chrome.
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 852);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 852);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [fulfillmentType, setFulfillmentType] = useState(null); // 'pickup' | 'delivery'
  const [pickupStore, setPickupStore] = useState(""); // Id of the selected store (from StoreAddress/Address)
  const [storeAddresses, setStoreAddresses] = useState([]); // [{ Country, Address: [...] }]
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);

  const [deliveryAddress, setDeliveryAddress] = useState({
    name: "",
    block: "",
    street: "",
    unit: "",
    postal: "",
  });
  const [deliveryAddressUid, setDeliveryAddressUid] = useState(""); // Uid of the selected/edited saved address
  const [addressList, setAddressList] = useState([]); // saved addresses from UserAddress/List
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [isDeletingAddress, setIsDeletingAddress] = useState(false);
  // phase: 'confirm' -> asking; 'success' | 'error' -> outcome of the delete call
  const [deleteAddressModal, setDeleteAddressModal] = useState({
    show: false,
    address: null,
    phase: "confirm",
    message: "",
  });
  const [addressPanel, setAddressPanel] = useState("closed"); // 'closed' | 'list' | 'form'

  const [paymentMethod, setPaymentMethod] = useState("coins"); // 'coins' or 'stripe'
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [formError, setFormError] = useState("");
  // Errors belonging to the address panel. Kept separate from formError so they
  // render next to the address form instead of down by the Place Order button.
  const [addressError, setAddressError] = useState("");
  const [savingAddress, setSavingAddress] = useState(false);

  // Surfaces the outcome of checkout even when the network hangs or is slow:
  // "processing" -> (optionally) "slow" -> "success" | "error" | "unknown".
  // "unknown" means a request timed out and we genuinely can't tell whether it
  // went through, so we don't falsely claim success or failure.
  const [txnStatusModal, setTxnStatusModal] = useState({
    show: false,
    phase: "processing",
    message: "",
  });
  const [insufficientGemsModal, setInsufficientGemsModal] = useState({
    show: false,
    required: 0,
    current: 0,
  });
  // Once the order goes through we empty the cart ourselves, so the
  // "empty cart" redirect below must not fire on the way to the receipt.
  const orderPlacedRef = useRef(false);

  /* ------------------------------ data loading ---------------------------- */

  const fetchCart = async () => {
    try {
      const [cartRes, productRes, drawsRes] = await Promise.all([
        fetch(`${API_BASE}/Usercart/GetUserCart?CustId=${userID}`),
        fetch(`${API_BASE}/member/getProduct`),
        fetch(`${API_BASE}/draws/GetListDraws`),
      ]);
      if (!cartRes.ok) throw new Error("Failed to fetch cart from server");

      const cartApiResult = await cartRes.json();
      if (!Array.isArray(cartApiResult) || cartApiResult.length === 0) {
        setCart([]);
        return;
      }

      const productData = productRes.ok ? await productRes.json() : [];
      const drawsData = drawsRes.ok ? await drawsRes.json() : [];

      const mappedCart = cartApiResult.map((item) => {
        const matched = Array.isArray(productData)
          ? productData.find((p) => p.ID === item.ProdId)
          : null;
        const isWheel = matched
          ? (matched.CATEGORY_DESC || "").toLowerCase().includes("wheel")
          : false;
        const isPhysical = matched
          ? !isWheel &&
            ["your style", "the living edit", "jewelry"].some((cat) =>
              (matched.CATEGORY_DESC || "").toLowerCase().includes(cat),
            )
          : false;

        // For wheel items, find the original draw ID from GetListDraws by matching name
        let drawId = null;
        if (isWheel && Array.isArray(drawsData)) {
          const draw = drawsData.find(
            (d) =>
              d.Description === item.ProdDesc ||
              d.Description === matched?.DESC_ENG,
          );
          drawId = draw ? draw.Id : null;
        }

        return {
          Uid: item.Uid,
          CustId: item.CustId,
          ID: item.ProdId,
          ProdId: item.ProdId,
          Name: item.ProdDesc,
          Remark: item.ProdRemarks,
          quantity: item.Qty,
          Qty: item.Qty,
          CardAmount: item.TotalAmount,
          GemAmount: item.GemTotal,
          IMAGE: item.ProdGif || (isWheel && matched ? matched.ImageUrl : null),
          isWheel,
          isPhysical,
          DrawId: drawId,
        };
      });

      setCart(mappedCart);
    } catch (error) {
      console.error("Error fetching cart:", error);
      setCart([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalCoins = async () => {
    try {
      const response = await fetch(`${API_BASE}/booking/getTotal?ID=${userID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        setCurrentCoins(0);
        return 0;
      }
      const coins = data[0].COINS;
      localStorage.setItem("coins", coins);
      setCurrentCoins(coins);
      return coins;
    } catch (error) {
      console.error("Error fetching data:", error);
      return 0;
    }
  };

  // Fetch the user's saved addresses (UserAddress/List). `silent` skips the
  // isLoadingAddress toggle, which otherwise swaps the whole address panel to
  // a "Loading addresses..." placeholder — fine for the first load, but a
  // jarring full-panel flash when we're just refreshing after a save/delete
  // the user already saw complete.
  const fetchAddressList = async ({ silent = false } = {}) => {
    if (!userID) return [];
    if (!silent) setIsLoadingAddress(true);
    try {
      const res = await fetch(`${API_BASE}/UserAddress/List?custId=${userID}`);
      const data = await res.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      setAddressList(list);
      return list;
    } catch (err) {
      console.error("Error fetching saved addresses:", err);
      return [];
    } finally {
      if (!silent) setIsLoadingAddress(false);
    }
  };

  useEffect(() => {
    fetchCart();
    fetchTotalCoins();
    fetchAddressList();
  }, []);

  // Fetch the list of pickup store addresses (StoreAddress/Address), grouped by country
  useEffect(() => {
    setIsLoadingStores(true);
    fetch(`${API_BASE}/StoreAddress/Address`)
      .then((res) => res.json())
      .then((data) => setStoreAddresses(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error fetching store addresses:", err))
      .finally(() => setIsLoadingStores(false));
  }, []);

  const findStoreById = (id) => {
    for (const group of storeAddresses) {
      const found = (group.Address || []).find((a) => a.Id === id);
      if (found) return { ...found, Country: found.Country || group.Country };
    }
    return null;
  };

  // Select a saved address as the delivery address
  const selectSavedAddress = (address) => {
    setDeliveryAddressUid(address.Uid || "");
    setDeliveryAddress({
      name: address.Contact || "",
      block: address.Block || "",
      street: address.Street || "",
      unit: address.UnitNo || "",
      postal: address.Postal || "",
    });
  };

  // Auto-select the first saved address once the list loads, so checkout has a default
  useEffect(() => {
    if (addressList.length > 0 && !deliveryAddressUid) {
      selectSavedAddress(addressList[0]);
    }
  }, [addressList]);

  const openAddAddress = () => {
    setAddressError("");
    setDeliveryAddressUid("");
    setDeliveryAddress({
      name: "",
      block: "",
      street: "",
      unit: "",
      postal: "",
    });
    setAddressPanel("form");
  };

  const openEditAddress = (address) => {
    setAddressError("");
    selectSavedAddress(address);
    setAddressPanel("form");
  };

  // Opens the confirmation modal for deleting a saved address
  const handleDeleteAddress = (address) => {
    if (!userID || !address?.Uid) return;
    setDeleteAddressModal({ show: true, address, phase: "confirm", message: "" });
  };

  // Actually deletes the address (StoreAddress/DeleteAddress), called once the
  // confirmation modal is accepted. The modal stays open and switches to a
  // success/error phase instead of closing, so the outcome is visible.
  const confirmDeleteAddress = async () => {
    const address = deleteAddressModal.address;
    if (!address?.Uid) return;

    const failMessage =
      t("deliveryAddress.addressDeleteError") ||
      "Failed to delete address. Please try again.";

    setIsDeletingAddress(true);
    const payload = [
      {
        Uid: address.Uid,
        CustId: Number(userID) || 0,
        Contact: "",
        Block: "",
        Street: "",
        UnitNo: "",
        Postal: "",
        Country: "",
        Default: 0,
      },
    ];

    try {
      const res = await fetch(
        `${API_BASE}/StoreAddress/DeleteAddress?Uid=${encodeURIComponent(address.Uid)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const text = await res.text();

      // The API answers HTTP 200 for both outcomes and reports the real
      // result in the body as { successful, Message } — same shape shown in
      // devtools. res.ok alone would report a rejected delete as a success.
      let parsed = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        // Non-JSON body; fall through to the res.ok check below.
      }
      const apiSuccessful =
        parsed && typeof parsed === "object" && "successful" in parsed
          ? Boolean(parsed.successful)
          : res.ok;

      if (!res.ok || !apiSuccessful) {
        console.error("Failed to delete address:", res.status, text);
        setDeleteAddressModal((prev) => ({
          ...prev,
          phase: "error",
          message: parsed?.Message || failMessage,
        }));
        return;
      }

      const list = await fetchAddressList({ silent: true });
      if (deliveryAddressUid === address.Uid) {
        if (list.length > 0) {
          selectSavedAddress(list[0]);
        } else {
          setDeliveryAddressUid("");
          setDeliveryAddress({ name: "", block: "", street: "", unit: "", postal: "" });
        }
      }

      setDeleteAddressModal((prev) => ({
        ...prev,
        phase: "success",
        message:
          parsed?.Message ||
          t("deliveryAddress.addressDeleted") ||
          "Address deleted successfully.",
      }));
    } catch (error) {
      console.error("Error deleting address:", error);
      setDeleteAddressModal((prev) => ({
        ...prev,
        phase: "error",
        message: failMessage,
      }));
    } finally {
      setIsDeletingAddress(false);
    }
  };

  // Add or update the user's delivery address (UserAddress/UpdateAddress)
  const saveUserAddress = async () => {
    const payload = {
      Uid: deliveryAddressUid || "",
      CustId: Number(userID) || 0,
      Contact: deliveryAddress.name,
      Block: deliveryAddress.block,
      Street: deliveryAddress.street,
      UnitNo: deliveryAddress.unit,
      Postal: deliveryAddress.postal,
      Country: "Singapore",
    };

    const failMessage =
      t("checkout.addressSaveFailed") ||
      "We couldn't save that address. Please check the details and try again.";

    const url = `${API_BASE}/UserAddress/UpdateAddress`;
    console.log("[SaveAddress] POST", url, payload);

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      console.log("[SaveAddress] response", {
        status: res.status,
        ok: res.ok,
        url: res.url,
        body: text,
      });

      if (!res.ok) {
        console.error("Failed to save delivery address:", res.status, text);
        return { ok: false, message: `${failMessage} (${res.status})` };
      }
      return { ok: true };
    } catch (error) {
      console.error("Error saving delivery address:", error);
      return {
        ok: false,
        message:
          t("checkout.addressSaveNetworkError") ||
          "We couldn't reach the server to save that address. Please check your connection and try again.",
      };
    }
  };

  // Save the address currently in the form, then return to the address list
  const handleSaveAddressForm = async () => {
    if (savingAddress) return;

    const { name, street, postal } = deliveryAddress;
    if (!name.trim() || !street.trim() || !postal.trim()) {
      setAddressError(
        t("checkout.addressIncomplete") ||
          "Please fill in all required delivery address fields.",
      );
      return;
    }

    setAddressError("");
    setFormError("");
    setSavingAddress(true);
    try {
      const result = await saveUserAddress();
      // A failed save used to leave the form sitting there with no explanation,
      // which read as the Save button doing nothing at all.
      if (!result.ok) {
        setAddressError(result.message);
        return;
      }

      const list = await fetchAddressList({ silent: true });
      const saved =
        list.find(
          (a) =>
            a.Contact === deliveryAddress.name &&
            a.Postal === deliveryAddress.postal,
        ) || list[0];
      if (saved) selectSavedAddress(saved);
      setAddressPanel("list");
    } finally {
      setSavingAddress(false);
    }
  };

  /* --------------------------------- totals ------------------------------- */

  const cartItems = cart;
  const hasPhysicalItems = cartItems.some((item) => item.isPhysical);

  // Physical carts default to pickup — it's free and needs no address, so it's
  // the lowest-friction option. Digital-only carts need no fulfillment at all.
  useEffect(() => {
    if (hasPhysicalItems && !fulfillmentType) setFulfillmentType("pickup");
  }, [hasPhysicalItems]);

  // Nothing to check out — send the user back to the cart, unless the cart is
  // empty precisely because we just placed the order.
  useEffect(() => {
    if (!loading && cartItems.length === 0 && !orderPlacedRef.current) {
      navigate("/cart", { replace: true, state: { from: "checkout" } });
    }
  }, [loading, cartItems.length]);

  const totalCard = cartItems.reduce(
    (sum, item) => sum + (item.CardAmount || 0),
    0,
  );
  const totalGems = cartItems.reduce(
    (sum, item) => sum + (item.GemAmount || 0),
    0,
  );
  const totalQty = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

  const selectedStore = findStoreById(pickupStore);

  // Free delivery qualification is based only on physical products; digital products are excluded.
  const totalPhysicalCard = cartItems.reduce(
    (sum, item) => sum + (item.isPhysical ? item.CardAmount || 0 : 0),
    0,
  );
  const chargesDelivery =
    hasPhysicalItems &&
    fulfillmentType === "delivery" &&
    totalPhysicalCard < FREE_DELIVERY_THRESHOLD_USD;
  const deliveryFeeUSD = chargesDelivery ? DELIVERY_FEE_USD : 0;
  const deliveryFeeGems = chargesDelivery ? DELIVERY_FEE_GEMS : 0;

  const grandTotalCard = totalCard + deliveryFeeUSD;
  const grandTotalGems = totalGems + deliveryFeeGems;
  const discountedGems =
    discountPercent > 0
      ? Math.round(grandTotalGems * (1 - discountPercent / 100))
      : grandTotalGems;

  /* -------------------------------- purchase ------------------------------ */

  // Runs `fn` (an async purchase request) behind the transaction status modal.
  // If `fn` hasn't settled within TXN_SLOW_THRESHOLD_MS, the modal switches to a
  // "this looks slow" message without cancelling the request. If `fn` itself
  // throws a TimeoutError (see fetchWithTimeout), we can't know whether the
  // server actually processed the purchase, so we surface that honestly as
  // "unknown" instead of claiming success or failure.
  const runTransaction = async (
    fn,
    { successMessage, errorMessage, unknownMessage } = {},
  ) => {
    let settled = false;
    setTxnStatusModal({
      show: true,
      phase: "processing",
      message: t("cart.txnProcessing") || "Processing your purchase...",
    });
    const slowTimer = setTimeout(() => {
      if (settled) return;
      setTxnStatusModal((prev) =>
        prev.show && prev.phase === "processing"
          ? {
              ...prev,
              phase: "slow",
              message:
                t("cart.txnSlow") ||
                "This is taking longer than usual — your connection may be slow. Please keep this window open.",
            }
          : prev,
      );
    }, TXN_SLOW_THRESHOLD_MS);

    try {
      const result = await fn();
      settled = true;
      clearTimeout(slowTimer);
      setTxnStatusModal({
        show: true,
        phase: "success",
        message:
          successMessage || t("cart.txnSuccess") || "🎉 Purchase successful!",
      });
      return result;
    } catch (err) {
      settled = true;
      clearTimeout(slowTimer);
      const isTimeout = err?.name === "TimeoutError";
      setTxnStatusModal({
        show: true,
        phase: isTimeout ? "unknown" : "error",
        message: isTimeout
          ? unknownMessage ||
            t("cart.txnUnknown") ||
            "We couldn't confirm your purchase due to a connection issue. Please check your balance or order history before trying again."
          : errorMessage || t("cart.txnError") || "❌ Purchase failed. Please try again.",
      });
      throw err;
    }
  };

  // Delete every row of the user's cart once the order has been recorded
  const deleteAllCartItems = async () => {
    try {
      const deletePromises = cart.map((item) =>
        fetch(`${API_BASE}/Usercart/UserCartDelete?UID=${item.Uid}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }),
      );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("❌ Error deleting cart items:", error);
    }
  };

  // Mark the user's cart row as completed (STATUS = 1)
  const updateCartStatus = async () => {
    try {
      const cartRes = await fetch(
        `${API_BASE}/Usercart/GetUserCart?CustId=${userID}`,
      );
      if (!cartRes.ok) throw new Error("Failed to fetch cart data");

      const cartData = await cartRes.json();
      if (!Array.isArray(cartData) || cartData.length === 0) {
        console.warn("No cart data to update");
        return;
      }

      const updatedCart = { ...cartData[0], STATUS: 1 };

      const response = await fetch(`${API_BASE}/Usercart/UserCartUpdate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedCart),
      });

      if (!response.ok) throw new Error("❌ Failed to update cart status");
    } catch (error) {
      console.error("❌ Error updating cart:", error);
    }
  };

  // Records the order. `method` is 'coins' or 'stripe'; for Stripe the card has
  // already been charged by the time this runs (see onPaymentSuccess below).
  const submitPurchase = async (method) => {
    console.log("[Purchase] submitPurchase called", {
      method,
      userID,
      confirmLoading,
      cartItemCount: cartItems.length,
    });
    if (confirmLoading) {
      console.warn(
        "[Purchase] ABORTED - confirmLoading was already true, no order was recorded",
      );
      return;
    }
    setConfirmLoading(true);

    const isStripe = method === "stripe";
    // Stripe already charges the card up-front, so a failure past this point
    // means the charge went through but our order recording failed — the
    // messaging needs to say that explicitly rather than implying nothing
    // happened.
    const orderErrorMessage = isStripe
      ? t("cart.stripeOrderError") ||
        "⚠️ Payment received, but we couldn't confirm your order. Please contact support."
      : t("cart.gemError") || "❌ Purchase failed. Please try again.";
    const orderUnknownMessage = isStripe
      ? t("cart.stripeOrderUnknown") ||
        "⚠️ Payment received, but confirming your order is taking too long due to a connection issue. Please check your order history in a moment, or contact support if it doesn't appear."
      : t("cart.gemUnknown") ||
        "We couldn't confirm your purchase due to a connection issue. Please check your gem balance before trying again.";

    try {
      await runTransaction(
        async () => {
          const wheelItems = cartItems.filter((item) => item.isWheel);
          const regularItems = cartItems.filter((item) => !item.isWheel);
          console.log("[Purchase] item split", {
            wheelItems: wheelItems.length,
            regularItems: regularItems.length,
            cartItems,
          });

          // Purchase wheel items via StickPurchase — single request with all items
          if (wheelItems.length > 0) {
            const payMode = isStripe ? 1 : 0;
            const stickBody = wheelItems.map((item) => ({
              Id: item.DrawId || item.ID,
              Qty: item.quantity || item.Qty || 1,
            }));
            const stickUrl = `${API_BASE}/draws/StickPurchase?CustId=${userID}&pay_mode=${payMode}`;
            console.log("[StickPurchase] POST", stickUrl, stickBody);
            const res = await fetchWithTimeout(
              stickUrl,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(stickBody),
              },
              TXN_TIMEOUT_MS,
            );
            const stickText = await res.text();
            console.log("[StickPurchase] response", {
              status: res.status,
              ok: res.ok,
              url: res.url,
              redirected: res.redirected,
              body: stickText,
            });
            assertApiSuccess("StickPurchase", res, stickText);
          }

          // Purchase regular items via PostPurchaseCoin
          if (regularItems.length > 0) {
            const items = regularItems.map((item) => ({
              CustomerId: Number(userID),
              pid: item.ID,
              Quantity: item.quantity || 1,
              Amount: isStripe ? item.CardAmount || 0 : item.GemAmount || 0,
            }));

            const addressPayload =
              fulfillmentType === "pickup"
                ? {
                    id: 0,
                    country: selectedStore?.Country || "",
                    contact: selectedStore?.Contact || "",
                    email: "",
                    city: selectedStore?.City || "",
                    province: selectedStore?.Province || "",
                    block: selectedStore?.Block || "",
                    street: selectedStore?.Street || "",
                    unitNo: selectedStore?.UnitNo || "",
                    postal: selectedStore?.Postal || "",
                    DeliveryMethod: fulfillmentType || "",
                    PickupStore: String(pickupStore || ""),
                    DeliveryFee: isStripe ? deliveryFeeUSD : deliveryFeeGems,
                  }
                : {
                    id: 0,
                    country: "Singapore",
                    contact: deliveryAddress.name || "",
                    email: "",
                    city: "",
                    province: "",
                    block: deliveryAddress.block || "",
                    street: deliveryAddress.street || "",
                    unitNo: deliveryAddress.unit || "",
                    postal: deliveryAddress.postal || "",
                    DeliveryMethod: fulfillmentType || "",
                    // "0", not "": an empty string fails to bind server-side and
                    // the whole order comes back as a ModelState error.
                    PickupStore: "0",
                    DeliveryFee: isStripe ? deliveryFeeUSD : deliveryFeeGems,
                  };

            const payload = {
              prefix: "PCH",
              PAY_MODE: isStripe ? 1 : 0,
              DeliveryAddress:
                fulfillmentType === "delivery"
                  ? JSON.stringify(deliveryAddress)
                  : "",
              Address: addressPayload,
              items,
            };

            const purchaseUrl = `${API_BASE}/member/PostPurchaseCoin`;
            console.log("[PostPurchaseCoin] POST", purchaseUrl);
            console.log(
              "[PostPurchaseCoin] payload",
              JSON.stringify(payload, null, 2),
            );

            const res = await fetchWithTimeout(
              purchaseUrl,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              },
              TXN_TIMEOUT_MS,
            );

            // Read as text first: a failing API often returns a non-JSON body,
            // and res.json() would throw that reason away.
            const purchaseText = await res.text();
            console.log("[PostPurchaseCoin] response", {
              status: res.status,
              ok: res.ok,
              url: res.url,
              redirected: res.redirected,
              body: purchaseText,
            });

            const parsed = assertApiSuccess(
              "PostPurchaseCoin",
              res,
              purchaseText,
              { requireSuccessWord: true },
            );
            console.log("[PostPurchaseCoin] parsed", parsed);
          }
        },
        {
          successMessage: isStripe
            ? t("cart.stripeSuccess") || "🎉 Card payment successful!"
            : t("cart.gemSuccess") || "🎉 Gem purchase successful!",
          errorMessage: orderErrorMessage,
          unknownMessage: orderUnknownMessage,
        },
      );

      console.log("[Purchase] transaction completed - clearing cart");
      // ✅ Only clear the cart AFTER a successful purchase
      orderPlacedRef.current = true;
      await updateCartStatus();
      await deleteAllCartItems();

      setCart([]);
      localStorage.removeItem("cart");

      window.dispatchEvent(new CustomEvent("cartUpdated"));
      window.dispatchEvent(new CustomEvent("gemsUpdated"));
    } catch (err) {
      console.error("Purchase failed:", err);
      throw err; // Re-throw so the Stripe callback can catch it
    } finally {
      setConfirmLoading(false);
      fetchTotalCoins(); // refresh the displayed balance regardless of outcome
    }
  };

  // What still has to be filled in before the order can be placed
  const validateOrder = () => {
    if (!hasPhysicalItems) return "";
    if (!fulfillmentType) {
      return (
        t("checkout.selectDeliveryOption") || "Please select a delivery option."
      );
    }
    if (fulfillmentType === "pickup" && !pickupStore) {
      return t("checkout.selectPickupStore") || "Please select a pickup store.";
    }
    if (fulfillmentType === "delivery") {
      const { name, street, postal } = deliveryAddress;
      if (!name.trim() || !street.trim() || !postal.trim()) {
        return (
          t("checkout.addressIncomplete") ||
          "Please add a complete delivery address."
        );
      }
    }
    return "";
  };

  const handlePlaceOrder = async () => {
    if (confirmLoading) return;

    const error = validateOrder();
    setFormError(error);
    if (error) return;

    // Persist the delivery address to the user's address book
    if (hasPhysicalItems && fulfillmentType === "delivery") {
      const result = await saveUserAddress();
      if (!result.ok) {
        setFormError(result.message);
        setAddressError(result.message);
        setAddressPanel("form");
        return;
      }
    }

    // Everything free — record the order without asking for payment
    const allFree = cartItems.every(
      (item) => (item.CardAmount || 0) === 0 && (item.GemAmount || 0) === 0,
    );
    if (allFree) {
      await submitPurchase("coins");
      return;
    }

    if (paymentMethod === "stripe") {
      setStripeModalOpen(true);
      return;
    }

    // Gems: check the live balance before committing
    const coins = await fetchTotalCoins();
    if (grandTotalGems > 0 && coins < grandTotalGems) {
      setInsufficientGemsModal({
        show: true,
        required: grandTotalGems,
        current: coins,
      });
      return;
    }

    await submitPurchase("coins");
  };

  const closeTxnModal = () => {
    const wasSuccess = txnStatusModal.phase === "success";
    setTxnStatusModal({ show: false, phase: "processing", message: "" });
    // Only a completed order moves the user on; a failed or unconfirmed one
    // leaves them on checkout with their cart intact.
    if (wasSuccess) navigate("/natal", { replace: true });
  };

  /* --------------------------------- render ------------------------------- */

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.wrapper}>
          <p style={{ color: GOLD }}>{t("checkout.loading") || "Loading checkout..."}</p>
        </div>
      </div>
    );
  }

  const deliveryOptions = [
    {
      id: "pickup",
      name: t("cart.inStorePickup") || "In-store Pickup",
      meta:
        t("checkout.pickupMeta") || "Collect your order at a Lotus Fairy store.",
      price: t("cart.free") || "Free",
    },
    {
      id: "delivery",
      name: t("checkout.standardDelivery") || "Standard Delivery",
      meta:
        t("cart.deliveryAvailableSingaporeOnlyPlain") ||
        "Delivery available for Singapore only.",
      price:
        totalPhysicalCard >= FREE_DELIVERY_THRESHOLD_USD
          ? t("cart.free") || "Free"
          : `US$${DELIVERY_FEE_USD.toFixed(2)} / ${DELIVERY_FEE_GEMS} ${t("cart.gems")}`,
    },
  ];
  const visibleOptions =
    showAllOptions || !fulfillmentType
      ? deliveryOptions
      : deliveryOptions.filter((o) => o.id === fulfillmentType);

  const hasAddress = Boolean(
    deliveryAddress.name && deliveryAddress.street && deliveryAddress.postal,
  );

  return (
    <>
      <div style={styles.page}>
        <div style={isDesktop ? styles.desktopWrapper : styles.wrapper}>
          {/* Header */}
          <div style={styles.header}>
            <button
              onClick={() => navigate("/cart", { state: { from: "checkout" } })}
              style={styles.backBtn}
              aria-label={t("cart.back") || "Back"}
            >
              ←
            </button>
            <h2 style={styles.heading}>{t("cart.checkout")}</h2>
          </div>

          <div style={isDesktop ? styles.desktopGrid : undefined}>
          <div style={isDesktop ? styles.leftColumn : undefined}>

          {/* Delivery address / pickup store */}
          {hasPhysicalItems && (
            <div style={styles.card}>
              <div
                onClick={() =>
                  fulfillmentType === "pickup"
                    ? setShowAllOptions(true)
                    : setAddressPanel(
                        addressPanel === "closed" ? "list" : "closed",
                      )
                }
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  cursor: "pointer",
                }}
              >
                <FaMapMarkerAlt style={{ fontSize: 18, lineHeight: 1.2, color: "#deaf77", flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {fulfillmentType === "pickup" ? (
                    <>
                      <div style={styles.optionName}>
                        {t("checkout.pickupAt") || "Pick up at"}
                        {selectedStore
                          ? ` ${selectedStore.City || selectedStore.Country}`
                          : ""}
                      </div>
                      <div style={styles.optionMeta}>
                        {selectedStore
                          ? formatStore(selectedStore)
                          : t("cart.selectPickupStore")}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={styles.optionName}>
                        {hasAddress
                          ? deliveryAddress.name
                          : t("deliveryAddress.noSavedAddress")}
                      </div>
                      <div style={styles.optionMeta}>
                        {hasAddress
                          ? formatDeliveryAddress(deliveryAddress)
                          : t("deliveryAddress.addAddress")}
                      </div>
                    </>
                  )}
                </div>
                <span style={{ color: GOLD_DIM, fontSize: 18 }}>›</span>
              </div>

              {/* Saved addresses / address form */}
              {fulfillmentType === "delivery" && addressPanel !== "closed" && (
                <div
                  style={{
                    marginTop: "14px",
                    paddingTop: "14px",
                    borderTop: "1px solid rgba(223,189,105,0.15)",
                  }}
                >
                  <p
                    style={{
                      color: GOLD_DIM,
                      margin: "0 0 12px",
                      fontSize: "13px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: t("cart.deliveryAvailableSingaporeOnly"),
                    }}
                  />

                  {isLoadingAddress ? (
                    <p style={{ color: GOLD_DIM, fontSize: "13px" }}>
                      {t("checkout.loadingAddresses") || "Loading addresses..."}
                    </p>
                  ) : addressPanel === "list" ? (
                    <div style={{ display: "grid", gap: "10px" }}>
                      {addressList.length === 0 ? (
                        <p style={{ color: GOLD_DIM, fontSize: "13px", margin: 0 }}>
                          {t("deliveryAddress.noSavedAddress")}
                        </p>
                      ) : (
                        addressList.map((address, index) => {
                          const isSelected =
                            deliveryAddressUid === (address.Uid || "");
                          return (
                            <div
                              key={address.Uid || index}
                              onClick={() => selectSavedAddress(address)}
                              style={{
                                ...selectableRow(isSelected),
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "10px",
                                }}
                              >
                                <input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => selectSavedAddress(address)}
                                  style={styles.radio}
                                />
                                <div>
                                  <div style={styles.optionName}>
                                    {address.Contact}
                                  </div>
                                  <div style={styles.optionMeta}>
                                    {formatAddress(address)}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditAddress(address);
                                  }}
                                  style={{
                                    background: "none",
                                    border: "1px solid rgba(223,189,105,0.3)",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    color: "rgba(223,189,105,0.8)",
                                    fontSize: "11px",
                                    padding: "4px 8px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {t("deliveryAddress.editAddress")}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAddress(address);
                                  }}
                                  disabled={isDeletingAddress}
                                  aria-label={
                                    t("deliveryAddress.deleteAddress") || "Delete address"
                                  }
                                  style={{
                                    background: "none",
                                    border: "1px solid rgba(255,95,95,0.5)",
                                    borderRadius: "6px",
                                    cursor: isDeletingAddress ? "not-allowed" : "pointer",
                                    color: "#FF665E",
                                    fontSize: "11px",
                                    padding: "4px 8px",
                                    flexShrink: 0,
                                    opacity: isDeletingAddress ? 0.6 : 1,
                                  }}
                                >
                                  {t("deliveryAddress.deleteAddress") || "Delete"}
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}

                      <button
                        onClick={openAddAddress}
                        style={{
                          width: "100%",
                          padding: "9px 12px",
                          borderRadius: "8px",
                          border: "1px dashed rgba(223,189,105,0.5)",
                          background: "transparent",
                          color: GOLD,
                          fontSize: "13px",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        + {t("deliveryAddress.addAddress")}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                          gap: "10px",
                        }}
                      >
                        <div style={{ gridColumn: "1 / -1" }}>
                          <label style={styles.inputLabel}>
                            {t("cart.fullName")} *
                          </label>
                          <input
                            type="text"
                            placeholder={t("cart.fullName")}
                            value={deliveryAddress.name}
                            onChange={(e) =>
                              setDeliveryAddress((p) => ({
                                ...p,
                                name: e.target.value,
                              }))
                            }
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.inputLabel}>
                            {t("deliveryAddress.blockBuilding")} *
                          </label>
                          <input
                            type="text"
                            placeholder="Blk 123"
                            value={deliveryAddress.block}
                            onChange={(e) =>
                              setDeliveryAddress((p) => ({
                                ...p,
                                block: e.target.value,
                              }))
                            }
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.inputLabel}>
                            {t("deliveryAddress.streetName")} *
                          </label>
                          <input
                            type="text"
                            placeholder={t("deliveryAddress.streetName")}
                            value={deliveryAddress.street}
                            onChange={(e) =>
                              setDeliveryAddress((p) => ({
                                ...p,
                                street: e.target.value,
                              }))
                            }
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.inputLabel}>
                            {t("deliveryAddress.unitNumber")}
                          </label>
                          <input
                            type="text"
                            placeholder="#01-01"
                            value={deliveryAddress.unit}
                            onChange={(e) =>
                              setDeliveryAddress((p) => ({
                                ...p,
                                unit: e.target.value,
                              }))
                            }
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.inputLabel}>
                            {t("deliveryAddress.postalCode")} *
                          </label>
                          <input
                            type="text"
                            placeholder="123456"
                            maxLength={6}
                            value={deliveryAddress.postal}
                            onChange={(e) =>
                              setDeliveryAddress((p) => ({
                                ...p,
                                postal: e.target.value.replace(/\D/g, ""),
                              }))
                            }
                            style={styles.input}
                          />
                        </div>
                        <div>
                          <label style={styles.inputLabel}>
                            {t("cart.country")}
                          </label>
                          <input
                            type="text"
                            value="Singapore"
                            disabled
                            style={{
                              ...styles.input,
                              opacity: 0.5,
                              cursor: "not-allowed",
                            }}
                          />
                        </div>
                      </div>
                      {addressError && (
                        <p
                          style={{
                            color: "#ff8a80",
                            fontSize: "12px",
                            margin: "12px 0 0",
                          }}
                        >
                          {addressError}
                        </p>
                      )}
                      <div
                        style={{ display: "flex", gap: "10px", marginTop: "14px" }}
                      >
                        {addressList.length > 0 && (
                          <button
                            onClick={() => {
                              setAddressError("");
                              setAddressPanel("list");
                            }}
                            style={{
                              flex: 1,
                              padding: "9px 12px",
                              borderRadius: "8px",
                              border: "1px solid rgba(223,189,105,0.35)",
                              background: "transparent",
                              color: GOLD,
                              fontSize: "13px",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            {t("cart.back")}
                          </button>
                        )}
                        <button
                          onClick={handleSaveAddressForm}
                          disabled={savingAddress}
                          style={{
                            flex: 1,
                            padding: "9px 12px",
                            borderRadius: "8px",
                            border: "none",
                            background: GOLD,
                            color: "#151517",
                            fontSize: "13px",
                            fontWeight: 700,
                            cursor: savingAddress ? "not-allowed" : "pointer",
                            opacity: savingAddress ? 0.6 : 1,
                          }}
                        >
                          {savingAddress
                            ? t("checkout.savingAddress") || "Saving..."
                            : t("deliveryAddress.saveAddress")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Items */}
          <div style={styles.card}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                paddingBottom: "12px",
                marginBottom: "4px",
                borderBottom: "1px solid rgba(223,189,105,0.15)",
              }}
            >
              {/* <span style={{ fontSize: 15 }}>🪷</span> */}
              <span style={{ ...styles.optionName, fontSize: "14px" }}>
                Cart Items ({totalQty})
              </span>
            </div>

            {cartItems.map((item, index) => (
              <div
                key={item.Uid || index}
                style={{
                  display: "flex",
                  gap: "12px",
                  padding: "14px 0",
                  borderBottom:
                    index < cartItems.length - 1
                      ? "1px solid rgba(255,255,255,0.06)"
                      : "none",
                }}
              >
                {item.IMAGE ? (
                  <img
                    src={item.IMAGE}
                    alt={item.Name}
                    style={{
                      width: "72px",
                      height: "72px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "8px",
                      background: "rgba(255,255,255,0.05)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: GOLD_DIM,
                      fontSize: "11px",
                      flexShrink: 0,
                    }}
                  >
                    {t("cart.noImage")}
                  </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...styles.optionName, fontSize: "14px" }}>
                    {item.Name}
                  </div>
                  {item.Remark && (
                    <div style={styles.optionMeta}>{item.Remark}</div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                      gap: "12px",
                      marginTop: "10px",
                    }}
                  >
                    <span style={{ color: GOLD, fontSize: "14px", fontWeight: 700 }}>
                      {t("cart.payment", {
                        cardAmount: Number(item.CardAmount || 0).toFixed(2),
                        gemAmount: item.GemAmount || 0,
                      })}
                    </span>
                    <span style={{ color: GOLD_DIM, fontSize: "13px" }}>
                      {t("cart.qty")}: {item.quantity || 1}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Delivery option */}
          {hasPhysicalItems && (
            <div style={styles.card}>
              <div style={styles.sectionTitleRow}>
                <h4 style={styles.sectionTitle}>
                  {t("checkout.deliveryOption") || "Delivery Option"}
                </h4>
                <button
                  onClick={() => setShowAllOptions((prev) => !prev)}
                  style={styles.linkBtn}
                >
                  {showAllOptions
                    ? t("checkout.hideOptions") || "Hide options"
                    : t("checkout.viewAllOptions") || "View all options"}
                  {" ›"}
                </button>
              </div>

              <div style={{ display: "grid", gap: "10px" }}>
                {visibleOptions.map((option) => (
                  <label
                    key={option.id}
                    style={selectableRow(fulfillmentType === option.id)}
                  >
                    <input
                      type="radio"
                      name="fulfillment"
                      checked={fulfillmentType === option.id}
                      onChange={() => {
                        setFulfillmentType(option.id);
                        setFormError("");
                        if (option.id === "delivery") setPickupStore("");
                        else setAddressPanel("closed");
                      }}
                      style={styles.radio}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "12px",
                        }}
                      >
                        <span style={styles.optionName}>{option.name}</span>
                        <span style={styles.optionName}>{option.price}</span>
                      </div>
                      <div style={styles.optionMeta}>{option.meta}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Pickup store selector */}
              {fulfillmentType === "pickup" && (
                <div
                  style={{
                    marginTop: "14px",
                    paddingTop: "14px",
                    borderTop: "1px solid rgba(223,189,105,0.15)",
                  }}
                >
                  <p
                    style={{
                      color: GOLD_DIM,
                      margin: "0 0 10px",
                      fontSize: "13px",
                    }}
                  >
                    {t("cart.selectPickupStore")}
                  </p>
                  {isLoadingStores ? (
                    <p style={{ color: GOLD_DIM, fontSize: "13px" }}>
                      {t("checkout.loadingStores") || "Loading stores..."}
                    </p>
                  ) : storeAddresses.length === 0 ? (
                    <p style={{ color: GOLD_DIM, fontSize: "13px", margin: 0 }}>
                      {t("checkout.noStores") || "No pickup stores available."}
                    </p>
                  ) : (
                    <div style={{ display: "grid", gap: "16px" }}>
                      {storeAddresses.map((group) => (
                        <div key={group.Country}>
                          <div
                            style={{
                              ...styles.optionName,
                              marginBottom: "8px",
                            }}
                          >
                            {group.Country}
                          </div>
                          <div style={{ display: "grid", gap: "10px" }}>
                            {(group.Address || []).map((address) => {
                              const isSelected = pickupStore === address.Id;
                              return (
                                <label
                                  key={address.Id}
                                  style={selectableRow(isSelected)}
                                >
                                  <input
                                    type="radio"
                                    name="pickupStore"
                                    checked={isSelected}
                                    onChange={() => {
                                      setPickupStore(address.Id);
                                      setFormError("");
                                    }}
                                    style={styles.radio}
                                  />
                                  <div>
                                    <div style={styles.optionName}>
                                      {address.City || group.Country}
                                      {address.Contact
                                        ? ` — ${address.Contact}`
                                        : ""}
                                    </div>
                                    <div style={styles.optionMeta}>
                                      {formatStore({
                                        ...address,
                                        Country: "",
                                      })}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          </div>

          <div style={isDesktop ? styles.rightColumn : undefined}>

          {/* Payment method */}
          <div style={styles.card}>
            <div style={styles.sectionTitleRow}>
              <h4 style={styles.sectionTitle}>
                {t("checkout.selectPaymentMethod") || "Select payment method"}
              </h4>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "10px",
              }}
            >
              <label style={selectableRow(paymentMethod === "coins")}>
                <input
                  type="radio"
                  name="paymentMethod"
                  checked={paymentMethod === "coins"}
                  onChange={() => setPaymentMethod("coins")}
                  style={styles.radio}
                />
                <div>
                  <div style={styles.optionName}>{t("cart.payWithGems")}</div>
                  <div style={styles.optionMeta}>
                    {t("checkout.gemBalance") || "Balance"}: {currentCoins}{" "}
                    {t("cart.gems")}
                  </div>
                </div>
              </label>

              <label style={selectableRow(paymentMethod === "stripe")}>
                <input
                  type="radio"
                  name="paymentMethod"
                  checked={paymentMethod === "stripe"}
                  onChange={() => setPaymentMethod("stripe")}
                  style={styles.radio}
                />
                <div>
                  <div style={styles.optionName}>{t("cart.payWithCard")}</div>
                  <div style={styles.optionMeta}>
                    US${Number(grandTotalCard).toFixed(2)}
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Order summary */}
          <div style={styles.card}>
            <div style={styles.sectionTitleRow}>
              <h4 style={styles.sectionTitle}>{t("cart.orderSummary")}</h4>
            </div>

            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>
                {t("checkout.merchandiseSubtotal")} ({totalQty}{" "}
                {t("checkout.items")})
              </span>
              <span style={styles.summaryValue}>
                US${Number(totalCard).toFixed(2)} / {totalGems} {t("cart.gems")}
              </span>
            </div>

            {hasPhysicalItems && fulfillmentType === "delivery" && (
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>{t("cart.deliveryFee")}</span>
                <span
                  style={{
                    ...styles.summaryValue,
                    color: deliveryFeeUSD > 0 ? GOLD_DIM : "#6ee7b7",
                  }}
                >
                  {deliveryFeeUSD > 0
                    ? `US$${deliveryFeeUSD.toFixed(2)} / ${deliveryFeeGems} ${t("cart.gems")}`
                    : t("cart.free")}
                </span>
              </div>
            )}

            {discountPercent > 0 && (
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>
                  {t("cart.discount")}({discountPercent}%)
                </span>
                <span style={{ ...styles.summaryValue, color: "#6ee7b7" }}>
                  -{grandTotalGems - discountedGems} {t("cart.gems")}
                </span>
              </div>
            )}

            <div
              style={{
                height: "1px",
                background: "rgba(223,189,105,0.2)",
                margin: "12px 0",
              }}
            />

            <div style={{ ...styles.summaryRow, marginBottom: 0, color: GOLD }}>
              <span style={{ ...styles.summaryLabel, fontWeight: 700 }}>
                {t("cart.grandTotal")}
              </span>
              <span style={{ ...styles.summaryValue, fontWeight: 700 }}>
                {paymentMethod === "stripe"
                  ? `US$${Number(grandTotalCard).toFixed(2)}`
                  : `${discountedGems} ${t("cart.gems")}`}
              </span>
            </div>

            <p style={{ color: GOLD_DIM, fontSize: "12px", margin: "10px 0 0" }}>
              {t("checkout.exchangeRate") || "Exchange Rate"}: 1$ ={" "}
              {EXCHANGE_RATE} {t("cart.gems")}
            </p>

            {/* Desktop: the Place Order action lives in-flow at the bottom of
                the sticky sidebar, instead of pinned to the viewport, so the
                page (and the site footer) can scroll normally underneath it. */}
            {isDesktop && (
              <div
                style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: "1px solid rgba(223,189,105,0.2)",
                }}
              >
                {formError && (
                  <div
                    style={{ color: "#f87171", fontSize: "12px", marginBottom: "10px" }}
                  >
                    {formError}
                  </div>
                )}
                <button
                  onClick={handlePlaceOrder}
                  disabled={confirmLoading}
                  style={{
                    width: "100%",
                    padding: "13px 28px",
                    backgroundColor: GOLD,
                    color: "#151517",
                    border: "none",
                    borderRadius: "28px",
                    fontWeight: "bold",
                    fontSize: "15px",
                    cursor: confirmLoading ? "not-allowed" : "pointer",
                    opacity: confirmLoading ? 0.6 : 1,
                  }}
                >
                  {confirmLoading
                    ? t("cart.processing")
                    : t("checkout.placeOrder") || "Place Order"}
                </button>
              </div>
            )}
          </div>

          </div>
          </div>
        </div>

        {/* Sticky Place Order bar — mobile only; on desktop the same action
            lives in the order summary card above instead. */}
        {!isDesktop && (
          <div style={styles.footerBar}>
            <div style={styles.footerInner}>
            <div>
              {formError && (
                <div
                  style={{
                    color: "#f87171",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  {formError}
                </div>
              )}
              <span style={{ color: GOLD_DIM, fontSize: "13px" }}>
                {t("cart.total")}:{" "}
              </span>
              <span style={{ color: GOLD, fontSize: "20px", fontWeight: 800 }}>
                {paymentMethod === "stripe"
                  ? `US$${Number(grandTotalCard).toFixed(2)}`
                  : `${discountedGems} ${t("cart.gems")}`}
              </span>
            </div>
            <button
              onClick={handlePlaceOrder}
              disabled={confirmLoading}
              style={{
                padding: "13px 28px",
                backgroundColor: GOLD,
                color: "#151517",
                border: "none",
                borderRadius: "28px",
                fontWeight: "bold",
                fontSize: "15px",
                cursor: confirmLoading ? "not-allowed" : "pointer",
                opacity: confirmLoading ? 0.6 : 1,
                minWidth: "160px",
              }}
            >
              {confirmLoading
                ? t("cart.processing")
                : t("checkout.placeOrder") || "Place Order"}
              </button>
            </div>
          </div>
        )}
      </div>

      <StripePayment
        id={userID}
        amount={grandTotalCard}
        currency="usd"
        isOpen={stripeModalOpen}
        onClose={() => setStripeModalOpen(false)}
        onPaymentSuccess={async () => {
          console.log("[Stripe] onPaymentSuccess fired - recording order");
          try {
            await submitPurchase("stripe");
          } catch (error) {
            // submitPurchase already surfaced the failure via the transaction
            // status modal, so there's nothing more to show here.
            console.error("Error after Stripe payment:", error);
          } finally {
            setStripeModalOpen(false);
          }
        }}
      />

      {/* Transaction status */}
      {txnStatusModal.show && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            // Higher than the Stripe modal's portal (zIndex 9999999) so the
            // outcome is visible even if it resolves while that modal is open.
            zIndex: 100000000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#111",
              color: GOLD,
              borderRadius: 12,
              padding: "28px 24px",
              width: "min(400px, 92vw)",
              textAlign: "center",
              boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
            }}
          >
            {(txnStatusModal.phase === "processing" ||
              txnStatusModal.phase === "slow") && (
              <div
                style={{
                  margin: "0 auto 14px",
                  width: 36,
                  height: 36,
                  border: "3px solid rgba(223,189,105,0.25)",
                  borderTopColor: GOLD,
                  borderRadius: "50%",
                  animation: "checkoutTxnSpin 0.8s linear infinite",
                }}
              />
            )}
            {txnStatusModal.phase === "success" && (
              <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
            )}
            {txnStatusModal.phase === "error" && (
              <div style={{ fontSize: 40, marginBottom: 8 }}>❌</div>
            )}
            {txnStatusModal.phase === "unknown" && (
              <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
            )}
            <p style={{ margin: 0, lineHeight: 1.5 }}>{txnStatusModal.message}</p>
            {txnStatusModal.phase !== "processing" &&
              txnStatusModal.phase !== "slow" && (
                <button
                  onClick={closeTxnModal}
                  style={{ ...styles.modalBtn, marginTop: 18 }}
                >
                  {t("cart.ok") || "OK"}
                </button>
              )}
          </div>
          <style>{`
            @keyframes checkoutTxnSpin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Delete address confirmation / outcome */}
      {deleteAddressModal.show && (
        <div
          style={styles.modalOverlay}
          onClick={() =>
            deleteAddressModal.phase === "confirm" && isDeletingAddress
              ? null
              : setDeleteAddressModal({
                  show: false,
                  address: null,
                  phase: "confirm",
                  message: "",
                })
          }
        >
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            {deleteAddressModal.phase === "confirm" && (
              <>
                <h2 style={{ color: GOLD, marginBottom: "16px" }}>
                  {t("deliveryAddress.deleteAddress") || "Delete Address"}
                </h2>
                <p style={{ color: GOLD, marginBottom: "20px", fontSize: "15px" }}>
                  {t("deliveryAddress.confirmDeleteAddress") ||
                    "Are you sure you want to delete this address?"}
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() =>
                      setDeleteAddressModal({
                        show: false,
                        address: null,
                        phase: "confirm",
                        message: "",
                      })
                    }
                    disabled={isDeletingAddress}
                    style={{
                      ...styles.modalBtn,
                      background: "transparent",
                      border: "1px solid rgba(223,189,105,0.4)",
                      color: GOLD,
                      opacity: isDeletingAddress ? 0.6 : 1,
                      cursor: isDeletingAddress ? "not-allowed" : "pointer",
                    }}
                  >
                    {t("cart.close") || "Cancel"}
                  </button>
                  <button
                    onClick={confirmDeleteAddress}
                    disabled={isDeletingAddress}
                    style={{
                      ...styles.modalBtn,
                      background: "#FF665E",
                      color: "#151517",
                      opacity: isDeletingAddress ? 0.6 : 1,
                      cursor: isDeletingAddress ? "not-allowed" : "pointer",
                    }}
                  >
                    {isDeletingAddress
                      ? t("checkout.deleting") || "Deleting..."
                      : t("deliveryAddress.deleteAddress") || "Delete"}
                  </button>
                </div>
              </>
            )}

            {deleteAddressModal.phase === "success" && (
              <>
                <h2 style={{ color: GOLD, marginBottom: "16px" }}>
                  {t("deliveryAddress.deleted") || "Deleted"}
                </h2>
                <p style={{ color: GOLD, marginBottom: "20px", fontSize: "15px" }}>
                  {deleteAddressModal.message}
                </p>
                <button
                  onClick={() =>
                    setDeleteAddressModal({
                      show: false,
                      address: null,
                      phase: "confirm",
                      message: "",
                    })
                  }
                  style={styles.modalBtn}
                >
                  {t("cart.close") || "Close"}
                </button>
              </>
            )}

            {deleteAddressModal.phase === "error" && (
              <>
                <h2 style={{ color: "#FF665E", marginBottom: "16px" }}>
                  {t("deliveryAddress.deleteFailed") || "❌ Delete Failed"}
                </h2>
                <p style={{ color: GOLD, marginBottom: "20px", fontSize: "15px" }}>
                  {deleteAddressModal.message}
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() =>
                      setDeleteAddressModal({
                        show: false,
                        address: null,
                        phase: "confirm",
                        message: "",
                      })
                    }
                    style={{
                      ...styles.modalBtn,
                      background: "transparent",
                      border: "1px solid rgba(223,189,105,0.4)",
                      color: GOLD,
                    }}
                  >
                    {t("cart.close") || "Close"}
                  </button>
                  <button
                    onClick={() =>
                      setDeleteAddressModal((prev) => ({ ...prev, phase: "confirm", message: "" }))
                    }
                    style={{ ...styles.modalBtn, background: "#FF665E", color: "#151517" }}
                  >
                    {t("deliveryAddress.tryAgain") || "Try Again"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Insufficient Gems */}
      {insufficientGemsModal.show && (
        <div
          style={styles.modalOverlay}
          onClick={() =>
            setInsufficientGemsModal({ show: false, required: 0, current: 0 })
          }
        >
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: GOLD, marginBottom: "20px" }}>
              {t("cart.insufficientGems")}
            </h2>
            <p style={{ color: GOLD, marginBottom: "15px", fontSize: "16px" }}>
              {t("cart.insufficientGemsMessage")}
            </p>
            <div
              style={{
                backgroundColor: "#1f1f1f",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "20px",
              }}
            >
              <p style={{ color: GOLD, marginBottom: "10px" }}>
                <strong>{t("cart.required")}:</strong>{" "}
                {insufficientGemsModal.required} {t("cart.gems")}
              </p>
              <p style={{ color: GOLD }}>
                <strong>{t("cart.youHave")}:</strong>{" "}
                {insufficientGemsModal.current} {t("cart.gems")}
              </p>
            </div>
            <p
              style={{
                color: GOLD,
                marginBottom: "20px",
                fontSize: "14px",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() =>
                navigate("/account", { state: { setactiveTab: "purchasecoin" } })
              }
            >
              {t("cart.purchaseMoreGems")}
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => {
                  setInsufficientGemsModal({
                    show: false,
                    required: 0,
                    current: 0,
                  });
                  setPaymentMethod("stripe");
                  setStripeModalOpen(true);
                }}
                style={{ ...styles.modalBtn, background: "#a67c00", color: "#fff" }}
              >
                {t("cart.pwc")}
              </button>
              <button
                onClick={() =>
                  setInsufficientGemsModal({
                    show: false,
                    required: 0,
                    current: 0,
                  })
                }
                style={styles.modalBtn}
              >
                {t("cart.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Checkout;
